import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, MailX, Settings2 } from "lucide-react";
import { useTenant } from "../contexts/TenantContext";
import { fonts as defaultFonts } from "../styles/theme";

const REASONS = [
  { id: "too_many", label: "Too many emails" },
  { id: "not_relevant", label: "Content not relevant to me" },
  { id: "never_subscribed", label: "I never subscribed" },
  { id: "other", label: "Other" },
];

const FREQUENCIES = [
  { id: "weekly", label: "Weekly digest only" },
  { id: "monthly", label: "Monthly digest only" },
  { id: "all", label: "Keep current frequency" },
];

const TOPICS = [
  { id: "marketAlerts", label: "Market Alerts" },
  { id: "weeklyDigest", label: "Weekly Digest" },
  { id: "clientUpdates", label: "Client Updates" },
];

export default function Unsubscribe() {
  const tenant = useTenant();
  const sb = tenant.theme?.sidebar || {};
  const fonts = tenant.theme?.fonts || defaultFonts;
  const accent = tenant.newsletter?.accentColor || "#4E95D9";

  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // null | "unsubscribed" | "preferences_updated"
  const [error, setError] = useState(null);

  // Preference state
  const [frequency, setFrequency] = useState("all");
  const [topics, setTopics] = useState({ marketAlerts: true, weeklyDigest: true, clientUpdates: true });
  const [showUnsubscribe, setShowUnsubscribe] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonText, setReasonText] = useState("");

  const paramsValid = email && token;

  const handleSavePreferences = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          token,
          action: "update_preferences",
          emailFrequency: frequency,
          topicPreferences: topics,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to update preferences");
      setSuccess("preferences_updated");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnsubscribe = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          token,
          action: "unsubscribe",
          reason: reason || undefined,
          reasonText: reason === "other" ? reasonText : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Unsubscribe failed");
      setSuccess("unsubscribed");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const cardStyle = {
    background: "rgba(255,255,255,0.04)",
    border: `1px solid ${sb.border}`,
    borderRadius: 16,
    padding: 40,
    width: "100%",
    maxWidth: 520,
    boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
  };

  const sectionStyle = {
    background: "rgba(255,255,255,0.04)",
    border: `1px solid ${sb.border}`,
    borderRadius: 10,
    padding: "16px 20px",
    marginBottom: 20,
  };

  const labelStyle = {
    fontSize: 11,
    color: sb.textSecondary,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 10,
    fontWeight: 700,
  };

  const radioStyle = (selected) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 13,
    color: selected ? sb.text : sb.textSecondary,
    background: selected ? "rgba(255,255,255,0.06)" : "transparent",
    border: `1px solid ${selected ? accent + "60" : "transparent"}`,
    transition: "all 0.15s",
  });

  const checkboxStyle = (checked) => ({
    width: 16,
    height: 16,
    borderRadius: 3,
    border: `2px solid ${checked ? accent : sb.textSecondary}`,
    background: checked ? accent : "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    cursor: "pointer",
    transition: "all 0.15s",
  });

  const btnStyle = (primary) => ({
    width: "100%",
    padding: "14px 0",
    background: primary ? accent : "transparent",
    color: primary ? "#0a0c10" : sb.textSecondary,
    border: primary ? "none" : `1px solid ${sb.border}`,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 700,
    fontFamily: fonts.body,
    cursor: submitting ? "not-allowed" : "pointer",
    opacity: submitting ? 0.7 : 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "all 0.15s",
  });

  return (
    <div
      style={{
        height: "100vh",
        overflowY: "auto",
        background: `linear-gradient(180deg, ${sb.bg} 0%, ${sb.bgHover || sb.bg} 100%)`,
        fontFamily: fonts.body,
        color: sb.text,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "40px 20px",
        boxSizing: "border-box",
      }}
    >
      {/* Logo header */}
      <div style={{ marginBottom: 40, textAlign: "center" }}>
        <img
          src={tenant.logo}
          alt={tenant.name}
          style={{ width: 144, height: "auto", borderRadius: 8, marginBottom: 12 }}
        />
        <div
          style={{
            fontSize: 11,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: accent,
            fontWeight: 700,
          }}
        >
          {tenant.companyName}
        </div>
      </div>

      {/* Main card */}
      <div style={cardStyle}>
        {!paramsValid ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <XCircle size={40} color="#ef4444" style={{ marginBottom: 16 }} />
            <h2 style={{ fontFamily: fonts.heading, fontSize: 22, marginBottom: 8 }}>
              Invalid Link
            </h2>
            <p style={{ color: sb.textSecondary, fontSize: 14 }}>
              This link is invalid or incomplete. Please use the link from your email.
            </p>
          </div>
        ) : success === "unsubscribed" ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <CheckCircle2 size={40} color="#10b981" style={{ marginBottom: 16 }} />
            <h2 style={{ fontFamily: fonts.heading, fontSize: 22, marginBottom: 8 }}>
              Unsubscribed
            </h2>
            <p style={{ color: sb.textSecondary, fontSize: 14, marginBottom: 8 }}>
              <strong style={{ color: sb.text }}>{email}</strong> has been removed from our mailing list.
            </p>
            <p style={{ color: sb.textSecondary, fontSize: 13 }}>
              If this was a mistake, please contact{" "}
              <a href={`mailto:${tenant.contactEmail}`} style={{ color: accent }}>
                {tenant.contactEmail}
              </a>.
            </p>
          </div>
        ) : success === "preferences_updated" ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <CheckCircle2 size={40} color="#10b981" style={{ marginBottom: 16 }} />
            <h2 style={{ fontFamily: fonts.heading, fontSize: 22, marginBottom: 8 }}>
              Preferences Updated
            </h2>
            <p style={{ color: sb.textSecondary, fontSize: 14 }}>
              Your email preferences have been saved. You'll continue to receive emails
              based on your selected preferences.
            </p>
          </div>
        ) : (
          <>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <Settings2 size={36} color={sb.textSecondary} style={{ marginBottom: 16 }} />
              <h2 style={{ fontFamily: fonts.heading, fontSize: 22, marginBottom: 8 }}>
                Email Preferences
              </h2>
              <p style={{ color: sb.textSecondary, fontSize: 14 }}>
                Manage your email settings for <strong style={{ color: sb.text }}>{email}</strong>
              </p>
            </div>

            {/* Frequency preference */}
            <div style={sectionStyle}>
              <div style={labelStyle}>Email Frequency</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {FREQUENCIES.map((f) => (
                  <div
                    key={f.id}
                    style={radioStyle(frequency === f.id)}
                    onClick={() => setFrequency(f.id)}
                  >
                    <div
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        border: `2px solid ${frequency === f.id ? accent : sb.textSecondary}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {frequency === f.id && (
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: accent }} />
                      )}
                    </div>
                    {f.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Topic preferences */}
            <div style={sectionStyle}>
              <div style={labelStyle}>Topics</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {TOPICS.map((t) => (
                  <div
                    key={t.id}
                    style={radioStyle(topics[t.id])}
                    onClick={() => setTopics((prev) => ({ ...prev, [t.id]: !prev[t.id] }))}
                  >
                    <div style={checkboxStyle(topics[t.id])}>
                      {topics[t.id] && (
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <path d="M2 5l2 2 4-4" stroke="#0a0c10" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    {t.label}
                  </div>
                ))}
              </div>
            </div>

            <button onClick={handleSavePreferences} disabled={submitting} style={btnStyle(true)}>
              {submitting && <Loader2 size={16} className="spin" />}
              Save Preferences
            </button>

            {/* Unsubscribe section */}
            <div style={{ marginTop: 24, borderTop: `1px solid ${sb.border}`, paddingTop: 20 }}>
              {!showUnsubscribe ? (
                <button
                  onClick={() => setShowUnsubscribe(true)}
                  style={{ ...btnStyle(false), fontSize: 13, padding: "10px 0", color: sb.textSecondary }}
                >
                  <MailX size={14} />
                  Unsubscribe from all emails
                </button>
              ) : (
                <>
                  <div style={labelStyle}>Why are you leaving?</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 }}>
                    {REASONS.map((r) => (
                      <div key={r.id} style={radioStyle(reason === r.id)} onClick={() => setReason(r.id)}>
                        <div
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            border: `2px solid ${reason === r.id ? accent : sb.textSecondary}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {reason === r.id && (
                            <div style={{ width: 6, height: 6, borderRadius: "50%", background: accent }} />
                          )}
                        </div>
                        {r.label}
                      </div>
                    ))}
                  </div>

                  {reason === "other" && (
                    <textarea
                      value={reasonText}
                      onChange={(e) => setReasonText(e.target.value)}
                      placeholder="Please tell us more..."
                      style={{
                        width: "100%",
                        minHeight: 60,
                        padding: "8px 12px",
                        background: "rgba(255,255,255,0.04)",
                        border: `1px solid ${sb.border}`,
                        borderRadius: 6,
                        color: sb.text,
                        fontFamily: fonts.body,
                        fontSize: 13,
                        resize: "vertical",
                        marginBottom: 12,
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  )}

                  {error && (
                    <div
                      style={{
                        padding: "10px 14px",
                        marginBottom: 12,
                        borderRadius: 6,
                        background: "rgba(239,68,68,0.1)",
                        border: "1px solid rgba(239,68,68,0.3)",
                        color: "#ef4444",
                        fontSize: 13,
                      }}
                    >
                      {error}
                    </div>
                  )}

                  <button
                    onClick={handleUnsubscribe}
                    disabled={submitting}
                    style={{ ...btnStyle(false), borderColor: "#ef4444", color: "#ef4444" }}
                  >
                    {submitting && <Loader2 size={16} className="spin" />}
                    Confirm Unsubscribe
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div style={{ marginTop: 40, textAlign: "center", color: sb.textSecondary, fontSize: 11 }}>
        &copy; {new Date().getFullYear()} {tenant.legalName} All rights reserved.
      </div>    </div>
  );
}
